import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { Box } from "@mui-wrapper";
import { Checkmark3Icon, Button, FontWeights } from "@flat-ui";
import USFlagIcon from "@assets/icons/us-flag_24px.svg?react";
import EUFlagIcon from "@assets/icons/eu-flag_24px.svg?react";
import { ReactNode } from "react";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { SERVER_IDENTIFIER_LABEL } from "@constants/server-constants";

interface Props {
  /** List of server identifiers */
  servers: FaroCoreApiTypes.ServerIdentifier[];

  /** The selected server */
  selectedServer: FaroCoreApiTypes.ServerIdentifier | null;

  /** Triggers when user selects a server */
  onSelectServer(server: FaroCoreApiTypes.ServerIdentifier): void;

  /** Whether to show/hide the label */
  shouldShowLabel?: boolean;

  /** Whether the selector buttons should be disabled */
  isDisabled?: boolean;

  /** Optional component to show on the right side of the label */
  helpComponent?: ReactNode;
}

/** Contains the flags for corresponding server */
export const flagIcons: Record<FaroCoreApiTypes.ServerIdentifier, JSX.Element> =
  {
    ["sphere_2_com"]: <USFlagIcon />,
    ["sphere_2_eu"]: <EUFlagIcon />,
  };

/** Add ability to select server by rendering the active server list */
export function LoginServerSelector({
  servers,
  selectedServer,
  onSelectServer,
  helpComponent,
  shouldShowLabel = true,
  isDisabled = false,
}: Props): JSX.Element {
  return (
    <>
      {shouldShowLabel && (
        <UlmInputLabel title="Server" helpComponent={helpComponent} />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        {servers.map((server) => (
          <Button
            key={server}
            variant="ghost"
            onClick={() => onSelectServer(server)}
            disableFocusRipple={true}
            disableRipple={true}
            disableElevation={true}
            disableTouchRipple={true}
            disabled={isDisabled}
            sx={{
              // The `ghost` variant tints the text with the primary color and hides the border
              // while hovered, pressed and disabled. Those states are listed explicitly because they
              // are more specific than the idle one, so they would win over it otherwise.
              "&, &:hover, &:active, &.Mui-disabled": {
                border: "1px solid",
                borderColor: "input.enabledBorder",
                color:
                  selectedServer === server ? "primary.main" : "text.primary",
              },
              borderRadius: "0px",
              minHeight: "39px",
              flexGrow: 1,
              // Set as longhands so they win over the padding of the design system size variant.
              paddingTop: "6px",
              paddingBottom: "6px",
              paddingLeft: "10px",
              paddingRight: "8px",
              textAlign: "left",
              marginBottom: "24px",
              textTransform: "capitalize",
              fontSize: "14px",
              fontWeight: FontWeights.SemiBold,
              "&:first-of-type": {
                // Adjust first element border style
                borderRadius: "4px 0 0 4px",
              },
              "&:last-of-type": {
                // Adjust last element border style
                borderRadius: "0 4px 4px 0",
                borderLeft: "none",
              },
              // Push the label to the left and the checkmark to the right.
              justifyContent: "space-between",
            }}
            data-testid={`server-${
              selectedServer === server ? "active" : "inactive"
            }`}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {flagIcons[server]}
              {SERVER_IDENTIFIER_LABEL[server]}
            </Box>

            <Checkmark3Icon
              sx={{
                color: "primary.main",
                visibility: selectedServer === server ? "visible" : "hidden",
              }}
            />
          </Button>
        ))}
      </Box>
    </>
  );
}
